import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";

import { getSelectedBrandPath } from "@insite/client-framework/Store/Context/ContextSelectors";
import {
    BrandStateContext,
    getBrandProductLinesDataView,
    getBrandStateByPath,
} from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import loadProducts from "@insite/client-framework/Store/Data/Products/Handlers/LoadProducts";
import { getProductsDataView } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import { PageProps } from "@insite/mobius/Page";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { ProductCarouselProductStyles } from "@sunSource/Overrides/Components/ProductCarouselProduct";
import ProductCarouselProduct from "@sunSource/Overrides/Components/ProductCarouselProduct";
import React, { FC, useContext } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

type Props = ResolveThunks<typeof mapDispatchToProps> & PageProps & ReturnType<typeof mapStateToProps>;

const mapStateToProps = (state: ApplicationState) => {
    const { pathname } = getLocation(state);
    const brandPath = getSelectedBrandPath(state) || pathname;
    const brandState = getBrandStateByPath(state, brandPath);
    const productsDataView = getProductsDataView(state, {
        brandIds: brandState.value?.id || "",
        pageSize: 8,
    } as any);
    return {
        brandPath,
        brandState,
        productsDataView,
    };
};

const mapDispatchToProps = {
    loadProducts,
};

export interface BrandsDetailProductCarousalStyles {
    container?: InjectableCss;
    image?: LazyImageProps;
    heading?: TypographyProps;
    carouselProductStyles?: ProductCarouselProductStyles;
}

export const logoStyles: BrandsDetailProductCarousalStyles = {
    heading: {
        variant: "h1",
        css: css`
            overflow-wrap: break-word;
            word-wrap: break-word;
        `,
    },
    container: {
        css: css`
            width: 100%;
            margin: 15px;
        `,
    },
    image: {
        css: css`
            width: 100%;
            img {
                height: 100%;
            }
        `,
    },
};

const styles = logoStyles;

const BrandsDetailProductCarousal: FC<Props> = ({ brandPath, brandState, loadProducts, productsDataView }) => {
    const { value: brand } = useContext(BrandStateContext);
    const [brandsProducts, setBrandProducts] = React.useState<ProductModel[] | undefined>([]);

    React.useEffect(() => {
        if (brandState.value?.id) {
            loadProducts({ brandIds: brandState.value?.id, pageSize: 8 } as any);
        }
    }, [brandState.value?.id]);

    React.useEffect(() => {
        if (productsDataView.value?.length) {
            setBrandProducts(productsDataView.value);
        }
    }, [productsDataView.value?.length]);

    return (
        <StyledWrapper {...styles.container} data-test-selector="brandLogo">
            {brandsProducts?.map((item, index) => (
                <StyledWrapper key={index}></StyledWrapper>
            ))}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(BrandsDetailProductCarousal),
    definition: {
        group: "Brand Details",
        icon: "diamond",
        displayName: "Brand Product Carousal",
        allowedContexts: ["BrandDetailsPage"],
    },
};

export default widgetModule;
